import { useEffect, useState } from "react";
import type { ProductImage, StorefrontFamily } from "../types";
import { customerFamilyName } from "../types";

const SLIDE_MS = 5000;

/** The family's primary image: its default variant's, else the first variant's (images arrive primary-first). */
export function primaryImage(family: StorefrontFamily): ProductImage | null {
  return family.defaultImage ?? family.variants.find((variant) => variant.images[0])?.images[0] ?? null;
}

function usePrefersReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return reduced;
}

// Home hero slideshow of the storefront favourites: each slide is the family's
// primary image and opens that family. It pauses while hovered or focused and
// never moves on its own for people who ask for reduced motion.
export function HeroSlideshow({ families, familyPath }: { families: StorefrontFamily[]; familyPath: (family: StorefrontFamily) => string }) {
  const slides = families.flatMap((family) => { const image = primaryImage(family); return image ? [{ family, image }] : []; });
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = usePrefersReducedMotion();
  const count = slides.length;
  const current = count ? index % count : 0;
  useEffect(() => {
    if (paused || reduced || count < 2) return;
    const timer = window.setInterval(() => setIndex((value) => (value + 1) % count), SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [paused, reduced, count]);
  if (!count) return null;
  const name = (family: StorefrontFamily) => customerFamilyName(family) || family.variants[0]?.title || "Illogical Trader product";
  const show = (value: number) => setIndex((value + count) % count);
  return (
    <div
      className="hero-slideshow"
      role="region"
      aria-roledescription="carousel"
      aria-label="Favourite products"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false); }}
    >
      <div className="slides" aria-live={paused || reduced ? "polite" : "off"}>
        {slides.map(({ family, image }, position) => (
          <a
            key={family.id}
            className={`slide${position === current ? " active" : ""}`}
            href={familyPath(family)}
            role="group"
            aria-roledescription="slide"
            aria-label={`${position + 1} of ${count}: ${name(family)}`}
            aria-hidden={position !== current}
            tabIndex={position === current ? 0 : -1}
          >
            <img src={image.url} alt={image.alt || name(family)} loading="eager" />
            <span className="slide-caption">{name(family)}</span>
          </a>
        ))}
      </div>
      {count > 1 ? (
        <div className="slide-controls">
          <button type="button" className="slide-step" aria-label="Previous favourite" onClick={() => show(current - 1)}>‹</button>
          <div className="slide-dots">
            {slides.map(({ family }, position) => (
              <button key={family.id} type="button" aria-label={`Show ${name(family)}`} aria-current={position === current} onClick={() => show(position)} />
            ))}
          </div>
          <button type="button" className="slide-step" aria-label="Next favourite" onClick={() => show(current + 1)}>›</button>
        </div>
      ) : null}
    </div>
  );
}
